import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Globe,
  Search as SearchIcon,
  CreditCard,
  Truck,
  Mail,
  Wrench,
  Save,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Home as HomeIcon,
  MessageCircle,
  Send,
  ShieldCheck,
  Megaphone,
  LayoutGrid,
  Upload,
  Image as ImageIcon,
  FolderOpen,
  RotateCcw,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { IconPicker } from '../components/IconPicker'
import { ImageLibraryPicker } from '../components/ImageLibraryPicker'
import { apiGet, apiPost, apiPut } from '../lib/api'

type ConfigMap = Record<string, string>

type SeccionId =
  | 'sitio'
  | 'seo'
  | 'pagos'
  | 'aplazo'
  | 'envios'
  | 'email'
  | 'whatsapp'
  | 'mantenimiento'
  | 'home'
  | 'banner_principal'
  | 'banner_ad'
  | 'trust'

const SECCIONES: { id: SeccionId; title: string; desc: string; icon: typeof Globe }[] = [
  { id: 'sitio', title: 'Sitio', desc: 'Datos del negocio y redes', icon: Globe },
  { id: 'home', title: 'Home', desc: 'Hero, banners y categorías', icon: HomeIcon },
  { id: 'banner_principal', title: 'Banner principal', desc: 'TimeTravel — banner ancho de la home', icon: Megaphone },
  { id: 'banner_ad', title: 'Banners secundarios', desc: '2 cards bajo el carousel de ofertas', icon: LayoutGrid },
  { id: 'seo', title: 'SEO', desc: 'Metadatos y analíticas', icon: SearchIcon },
  { id: 'pagos', title: 'Pasarelas de pago', desc: 'PayPal, MP, Transferencia', icon: CreditCard },
  { id: 'aplazo', title: 'Aplazo', desc: 'SFTP de liquidaciones + timeout', icon: CreditCard },
  { id: 'envios', title: 'Envíos', desc: 'Costos y tiempos', icon: Truck },
  { id: 'email', title: 'Email transaccional', desc: 'SMTP y plantillas', icon: Mail },
  { id: 'whatsapp', title: 'WhatsApp Business', desc: 'API Cloud + envío de prueba', icon: MessageCircle },
  { id: 'trust', title: 'Trust y entrega', desc: 'Claims, horarios, garantías, cupones', icon: ShieldCheck },
  { id: 'mantenimiento', title: 'Mantenimiento', desc: 'Modo offline y banner', icon: Wrench },
]

// Claves de cada sección — se piden todas con ?keys=...
const KEYS_BY_SECCION: Record<SeccionId, string[]> = {
  sitio: [
    'Sitio_Nombre', 'Sitio_Eslogan', 'Sitio_URL', 'Sitio_Email', 'Sitio_Telefono',
    'Sitio_Whatsapp', 'Sitio_Logo', 'Sitio_Direccion',
    'Sitio_Facebook', 'Sitio_Instagram', 'Sitio_Youtube', 'Sitio_Tiktok', 'Sitio_Twitter',
  ],
  seo: [
    'SEO_Title', 'SEO_Description', 'SEO_Keywords',
    'SEO_GoogleAnalytics', 'SEO_GoogleTagManager',
    'SEO_FacebookPixel', 'SEO_GoogleSearchConsole',
    // OG image por defecto cuando un producto no tiene foto propia.
    'Brand_OgImageDefault',
    // Datos para JSON-LD Organization (schema.org). El web los lee
    // server-side y los inyecta en <head> dinámicamente.
    'Schema_Phone', 'Schema_SameAs',
    // Trackers y verifications inyectados en layout.tsx server-side. Si
    // están vacíos no se renderiza nada (no rompe el sitio).
    'SEO_GA4_MeasurementId', 'SEO_GSC_VerificationCode',
    'SEO_Bing_VerificationCode', 'SEO_FB_Pixel',
  ],
  pagos: [
    'Fdp_Paypal_Activo', 'Fdp_Paypal_Api_Version', 'Fdp_Paypal_Entorno',
    'Fdp_Paypal_ClientID_Live', 'Fdp_Paypal_Secret_Live',
    'Fdp_Paypal_ClientID_Sandbox', 'Fdp_Paypal_Secret_Sandbox', 'Fdp_Paypal_Webhook_ID',
    'Fdp_MP_Activo', 'Fdp_MP_Api_Version', 'Fdp_MP_Entorno',
    'Fdp_MP_Brick_PublicKey_Live', 'Fdp_MP_Brick_AccessToken_Live',
    'Fdp_MP_Brick_PublicKey_Sandbox', 'Fdp_MP_Brick_AccessToken_Sandbox',
    'Fdp_MP_Webhook_Secret',
    'Fdp_Transfer_Activo', 'Fdp_Transfer_Banco', 'Fdp_Transfer_Cuenta',
    'Fdp_Transfer_Clabe', 'Fdp_Transfer_Beneficiario', 'Fdp_Transfer_RFC',
  ],
  aplazo: [
    'Aplazo_SFTP_Host', 'Aplazo_SFTP_Port',
    'Aplazo_SFTP_User', 'Aplazo_SFTP_Pass',
    'Aplazo_SFTP_Path',
    'Aplazo_Timeout_Hours',
  ],
  envios: [
    'Envio_CostoDefault', 'Envio_GratisDesde',
    'Envio_DiasMin', 'Envio_DiasMax',
    // Guías automáticas (XFT / FedEx). Server-only; NO revalidan config.
    'XFT_ApiKey',
    'Shipping_Carrier_Estafeta_Rate', 'Shipping_Carrier_Fedex_Rate',
    'FedEx_ApiKey', 'FedEx_SecretKey', 'FedEx_AccountNumber',
    // Remitente Maz (origen de la guía)
    'Shipping_Sender_Name', 'Shipping_Sender_BusinessName', 'Shipping_Sender_Phone',
    'Shipping_Sender_Email', 'Shipping_Sender_Street', 'Shipping_Sender_Number',
    'Shipping_Sender_Neighborhood', 'Shipping_Sender_Zip', 'Shipping_Sender_City',
    'Shipping_Sender_State', 'Shipping_Sender_Country', 'Shipping_Sender_References',
    // Defaults de la caja / paquete
    'Shipping_Default_SatCategory', 'Shipping_Default_PackageType', 'Shipping_Default_Weight',
    'Shipping_Default_Length', 'Shipping_Default_Width', 'Shipping_Default_Height',
    'Shipping_Default_PackageValue',
    // Medidas globales de la caja por paquetería (usadas al generar la guía)
    'Shipping_Box_Estafeta_Weight', 'Shipping_Box_Estafeta_Length',
    'Shipping_Box_Estafeta_Width', 'Shipping_Box_Estafeta_Height',
    'Shipping_Box_Fedex_Weight', 'Shipping_Box_Fedex_Length',
    'Shipping_Box_Fedex_Width', 'Shipping_Box_Fedex_Height',
  ],
  email: [
    'Email_From', 'Email_AdminTo', 'Email_Resend_ApiKey',
    'Email_SMTP_Host', 'Email_SMTP_Port', 'Email_SMTP_User',
    'Email_SMTP_Pass', 'Email_SMTP_Secure',
  ],
  whatsapp: [
    'WA_API_URL', 'WA_PHONE_ID', 'WA_TOKEN', 'WA_OnShipped',
  ],
  mantenimiento: [
    'Mantenimiento_Activo', 'Mantenimiento_Titulo', 'Mantenimiento_Mensaje',
    'Mantenimiento_BotonWhatsApp', 'Mantenimiento_Footnote',
    'Banner_Texto',
  ],
  home: [
    'Home_Hero_Titulo', 'Home_Hero_Subtitulo',
    'Home_Hero_CTA1_Texto', 'Home_Hero_CTA2_Texto',
    'Home_Banner_Cobertura_Activo',
    'Home_Categorias_Mostrar',
    // Iconos del strip del footer (4 slots). Vacío = SVG hardcoded default.
    'Footer_Strip_Icon_Shipping',
    'Footer_Strip_Icon_Warranty',
    'Footer_Strip_Icon_Support',
    'Footer_Strip_Icon_Coupon',
  ],
  banner_principal: [
    // TimeTravelBanner — banner ancho con CTA bajo el carousel principal.
    'TimeTravel_Eyebrow',
    'TimeTravel_Title',
    'TimeTravel_CtaLabel',
    'TimeTravel_CtaUrl',
    'TimeTravel_Image',
  ],
  banner_ad: [
    // AdBanner — 2 cards side-by-side bajo el carousel de ofertas.
    'AdBanner1_Title', 'AdBanner1_Sub', 'AdBanner1_CtaLabel', 'AdBanner1_CtaUrl', 'AdBanner1_Image',
    'AdBanner2_Title', 'AdBanner2_Sub', 'AdBanner2_CtaLabel', 'AdBanner2_CtaUrl', 'AdBanner2_Image',
  ],
  trust: [
    // Cobertura y entrega
    'Site_DeliveryCoverage', 'Site_DeliveryTime', 'Site_DeliveryTimeMetro', 'Site_FreeShippingMin',
    // Horarios
    'Site_BusinessHoursShort', 'Site_BusinessHoursLong', 'Site_BusinessHoursSchema',
    // Garantías y políticas
    'Site_WarrantyDays', 'Site_ReturnDays', 'Site_TaxRate', 'Site_TaxLabel',
    // Cupones promocionales
    'Coupon_Welcome_Code', 'Coupon_Welcome_Discount', 'Coupon_Welcome_Label',
    // Catálogo metrics (visibles en marketing)
    'Catalog_TotalProductsClaim', 'Catalog_TotalStatesClaim',
    // Trust strip y claims
    'Trust_Free_Shipping', 'Trust_Protected_Purchase', 'Trust_Return_Days',
    'Trust_Secure_Payment', 'Trust_Real_Support',
    // Strip del footer (garantía + subtítulo de envío gratis)
    'Footer_Claim_Warranty', 'Footer_Claim_Warranty_Sub', 'Footer_FreeShippingSub',
    // Defaults legacy
    'Site_PhoneFallback', 'Site_WhatsAppFallback', 'Site_EmailFallback',
  ],
}

const ALL_KEYS = Object.values(KEYS_BY_SECCION).flat()

function isTrue(v?: string): boolean {
  if (!v) return false
  const s = v.toLowerCase().trim()
  return s === '1' || s === 'true' || s === 'si' || s === 'sí' || s === 'on' || s === 'activo'
}

export function Configuracion() {
  const qc = useQueryClient()
  const [seccion, setSeccion] = useState<SeccionId>('sitio')
  const [draft, setDraft] = useState<ConfigMap>({})
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-config', ALL_KEYS.join(',')],
    queryFn: () =>
      apiGet<{ data: ConfigMap }>(`/api/admin/config?keys=${encodeURIComponent(ALL_KEYS.join(','))}`),
  })

  const original = data?.data ?? {}

  // Inicializar el draft cuando se carga la data por primera vez
  useEffect(() => {
    if (data?.data) {
      setDraft((prev) => (Object.keys(prev).length === 0 ? { ...data.data } : prev))
    }
  }, [data])

  const dirtyKeys = useMemo(() => {
    return Object.keys(draft).filter((k) => (draft[k] ?? '') !== (original[k] ?? ''))
  }, [draft, original])

  const saveMut = useMutation({
    mutationFn: async () => {
      const changes: ConfigMap = {}
      for (const k of dirtyKeys) changes[k] = draft[k] ?? ''
      return apiPut<{ updated: number; keys: string[] }>('/api/admin/config', { changes })
    },
    onSuccess: (r) => {
      setFlash({ tipo: 'ok', msg: `${r.updated} clave(s) guardadas` })
      qc.invalidateQueries({ queryKey: ['admin-config'] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: any) => {
      setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar' })
    },
  })

  const testPaypalMut = useMutation({
    mutationFn: () => apiPost<{ ok: boolean; error?: string }>('/api/admin/config/test-paypal', {}),
    onSuccess: (r) =>
      setFlash({ tipo: r.ok ? 'ok' : 'err', msg: r.ok ? 'PayPal OK' : r.error || 'PayPal falló' }),
    onError: (e: any) => setFlash({ tipo: 'err', msg: e?.message || 'PayPal falló' }),
  })

  const testMpMut = useMutation({
    mutationFn: () => apiPost<{ ok: boolean; error?: string }>('/api/admin/config/test-mp', {}),
    onSuccess: (r) =>
      setFlash({ tipo: r.ok ? 'ok' : 'err', msg: r.ok ? 'MP OK' : r.error || 'MP falló' }),
    onError: (e: any) => setFlash({ tipo: 'err', msg: e?.message || 'MP falló' }),
  })

  function set(k: string, v: string) {
    setDraft((d) => ({ ...d, [k]: v }))
  }
  function get(k: string): string {
    return draft[k] ?? ''
  }
  function toggleSecret(k: string) {
    setShowSecrets((s) => ({ ...s, [k]: !s[k] }))
  }
  function discard() {
    setDraft({ ...original })
  }

  return (
    <>
      <Topbar
        title="Configuración"
        subtitle="Ajustes globales del sitio (clave-valor)"
        actions={
          <>
            {dirtyKeys.length > 0 && (
              <Badge variant="warning">{dirtyKeys.length} sin guardar</Badge>
            )}
            <button
              type="button"
              onClick={discard}
              disabled={dirtyKeys.length === 0}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={dirtyKeys.length === 0 || saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar cambios
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Sidebar de secciones */}
          <nav className="space-y-2 lg:col-span-1 lg:sticky lg:top-28 lg:self-start">
            {SECCIONES.map((s) => {
              const Icon = s.icon
              const activo = seccion === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSeccion(s.id)}
                  className={`flex w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left transition-all ${
                    activo
                      ? 'border-brand-300 ring-2 ring-brand-100'
                      : 'border-ink-200 hover:border-ink-300 hover:shadow-sm'
                  }`}
                >
                  <div
                    className={`grid size-9 place-items-center rounded-xl ${
                      activo ? 'bg-brand-600 text-white' : 'bg-brand-50 text-brand-600'
                    }`}
                  >
                    <Icon className="size-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink-900">{s.title}</div>
                    <div className="text-xs text-ink-500">{s.desc}</div>
                  </div>
                </button>
              )
            })}
          </nav>

          {/* Detalle */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-ink-200 bg-white">
              {isLoading ? (
                <div className="flex items-center gap-2 p-10 text-ink-500">
                  <Loader2 className="size-4 animate-spin" /> Cargando configuración…
                </div>
              ) : (
                <>
                  {seccion === 'sitio' && <SecSitio get={get} set={set} />}
                  {seccion === 'home' && <SecHome get={get} set={set} />}
                  {seccion === 'seo' && <SecSeo get={get} set={set} />}
                  {seccion === 'pagos' && (
                    <SecPagos
                      get={get}
                      set={set}
                      showSecrets={showSecrets}
                      toggleSecret={toggleSecret}
                      onTestPaypal={() => testPaypalMut.mutate()}
                      onTestMp={() => testMpMut.mutate()}
                      paypalLoading={testPaypalMut.isPending}
                      mpLoading={testMpMut.isPending}
                    />
                  )}
                  {seccion === 'aplazo' && (
                    <SecAplazo
                      get={get}
                      set={set}
                      showSecrets={showSecrets}
                      toggleSecret={toggleSecret}
                    />
                  )}
                  {seccion === 'envios' && (
                    <SecEnvios
                      get={get}
                      set={set}
                      showSecrets={showSecrets}
                      toggleSecret={toggleSecret}
                    />
                  )}
                  {seccion === 'email' && (
                    <SecEmail
                      get={get}
                      set={set}
                      showSecrets={showSecrets}
                      toggleSecret={toggleSecret}
                    />
                  )}
                  {seccion === 'whatsapp' && (
                    <SecWhatsApp
                      get={get}
                      set={set}
                      showSecrets={showSecrets}
                      toggleSecret={toggleSecret}
                    />
                  )}
                  {seccion === 'banner_principal' && <SecBannerPrincipal get={get} set={set} />}
                  {seccion === 'banner_ad' && <SecBannerAd get={get} set={set} />}
                  {seccion === 'trust' && <SecTrust get={get} set={set} />}
                  {seccion === 'mantenimiento' && <SecMantenimiento get={get} set={set} />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* ---------- componentes auxiliares de form ---------- */

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
        {desc && <p className="text-xs text-ink-500">{desc}</p>}
      </div>
      {children}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  mono = false,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  mono?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 ${
          mono ? 'font-mono text-xs' : ''
        }`}
      />
    </div>
  )
}

function SecretField({
  label,
  value,
  onChange,
  visible,
  onToggle,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  visible: boolean
  onToggle: () => void
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 pr-9 font-mono text-xs outline-none focus:border-brand-500"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-700"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  )
}

function Textarea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
      />
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function Switch({
  label,
  checked,
  onChange,
}: {
  label?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <span className="relative inline-block h-6 w-11">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-ink-200 transition-colors peer-checked:bg-brand-600" />
        <span className="absolute left-0.5 top-0.5 size-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
      </span>
      {label && <span className="text-sm text-ink-700">{label}</span>}
    </label>
  )
}

/* ---------- sección: Sitio ---------- */

function SecSitio({ get, set }: { get: (k: string) => string; set: (k: string, v: string) => void }) {
  return (
    <Section title="Sitio" desc="Datos del negocio y presencia en redes">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Nombre del sitio" value={get('Sitio_Nombre')} onChange={(v) => set('Sitio_Nombre', v)} />
        <Field label="Eslogan" value={get('Sitio_Eslogan')} onChange={(v) => set('Sitio_Eslogan', v)} />
        <Field label="URL del sitio" value={get('Sitio_URL')} onChange={(v) => set('Sitio_URL', v)} placeholder="https://…" />
        <Field label="Email de contacto" value={get('Sitio_Email')} onChange={(v) => set('Sitio_Email', v)} type="email" />
        <Field label="Teléfono" value={get('Sitio_Telefono')} onChange={(v) => set('Sitio_Telefono', v)} />
        <Field label="WhatsApp" value={get('Sitio_Whatsapp')} onChange={(v) => set('Sitio_Whatsapp', v)} placeholder="+52…" />
        <div className="sm:col-span-2">
          <Field label="Logo (URL)" value={get('Sitio_Logo')} onChange={(v) => set('Sitio_Logo', v)} mono />
        </div>
        <div className="sm:col-span-2">
          <Textarea label="Dirección física principal" value={get('Sitio_Direccion')} onChange={(v) => set('Sitio_Direccion', v)} rows={2} />
        </div>
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Redes sociales</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Facebook" value={get('Sitio_Facebook')} onChange={(v) => set('Sitio_Facebook', v)} mono />
        <Field label="Instagram" value={get('Sitio_Instagram')} onChange={(v) => set('Sitio_Instagram', v)} mono />
        <Field label="YouTube" value={get('Sitio_Youtube')} onChange={(v) => set('Sitio_Youtube', v)} mono />
        <Field label="TikTok" value={get('Sitio_Tiktok')} onChange={(v) => set('Sitio_Tiktok', v)} mono />
        <Field label="Twitter / X" value={get('Sitio_Twitter')} onChange={(v) => set('Sitio_Twitter', v)} mono />
      </div>
    </Section>
  )
}

/* ---------- sección: SEO ---------- */

function SecSeo({ get, set }: { get: (k: string) => string; set: (k: string, v: string) => void }) {
  return (
    <Section title="SEO" desc="Metadatos globales y analíticas">
      <div className="grid grid-cols-1 gap-3">
        <Field label="Meta title default" value={get('SEO_Title')} onChange={(v) => set('SEO_Title', v)} />
        <Textarea label="Meta description default" value={get('SEO_Description')} onChange={(v) => set('SEO_Description', v)} rows={2} />
        <Textarea label="Meta keywords" value={get('SEO_Keywords')} onChange={(v) => set('SEO_Keywords', v)} rows={2} />
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Tracking (legacy)</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Google Analytics ID" value={get('SEO_GoogleAnalytics')} onChange={(v) => set('SEO_GoogleAnalytics', v)} mono placeholder="G-XXXXXX" />
        <Field label="Google Tag Manager ID" value={get('SEO_GoogleTagManager')} onChange={(v) => set('SEO_GoogleTagManager', v)} mono placeholder="GTM-XXXXX" />
        <Field label="Facebook Pixel ID" value={get('SEO_FacebookPixel')} onChange={(v) => set('SEO_FacebookPixel', v)} mono />
        <Field label="Google Search Console (verification)" value={get('SEO_GoogleSearchConsole')} onChange={(v) => set('SEO_GoogleSearchConsole', v)} mono />
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">
        Analytics y verifications (live en producción)
      </h3>
      <p className="mb-3 text-xs text-ink-500">
        Estos campos se inyectan directamente en el <span className="font-mono">&lt;head&gt;</span> y body del sitio
        público. Si están vacíos no se renderiza nada (no rompe el sitio). Útiles para activar
        GA4, Search Console, Bing Webmaster y Facebook Pixel sin tocar código.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="GA4 Measurement ID"
          value={get('SEO_GA4_MeasurementId')}
          onChange={(v) => set('SEO_GA4_MeasurementId', v)}
          mono
          placeholder="G-XXXXXXXXXX"
        />
        <Field
          label="Google Search Console — código de verificación"
          value={get('SEO_GSC_VerificationCode')}
          onChange={(v) => set('SEO_GSC_VerificationCode', v)}
          mono
          placeholder="abc123…"
        />
        <Field
          label="Bing Webmaster — código de verificación"
          value={get('SEO_Bing_VerificationCode')}
          onChange={(v) => set('SEO_Bing_VerificationCode', v)}
          mono
          placeholder="msvalidate.01"
        />
        <Field
          label="Facebook Pixel ID"
          value={get('SEO_FB_Pixel')}
          onChange={(v) => set('SEO_FB_Pixel', v)}
          mono
          placeholder="1234567890"
        />
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">
        Open Graph y JSON-LD
      </h3>
      <p className="mb-3 text-xs text-ink-500">
        Imagen que se comparte en Facebook/Twitter/WhatsApp cuando un producto
        no tiene foto propia. Los productos con foto siguen usando la suya;
        las páginas estáticas usan ésta. Recomendado 1200×630 PNG/JPG.
      </p>
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="OG image por defecto (URL absoluta)"
          value={get('Brand_OgImageDefault')}
          onChange={(v) => set('Brand_OgImageDefault', v)}
          placeholder="https://mazautopartes.com.mx/og-default.jpg"
          mono
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Teléfono para schema.org (formato E.164)"
            value={get('Schema_Phone')}
            onChange={(v) => set('Schema_Phone', v)}
            placeholder="+523312345678"
            mono
          />
          <Field
            label="sameAs (JSON array de URLs sociales)"
            value={get('Schema_SameAs')}
            onChange={(v) => set('Schema_SameAs', v)}
            placeholder='["https://facebook.com/...","https://instagram.com/..."]'
            mono
          />
        </div>
        <p className="text-xs text-ink-500">
          Si <span className="font-mono">Schema_SameAs</span> está vacío, el
          sitio cae a las URLs configuradas en la sección Sitio (Facebook,
          Instagram, YouTube, TikTok, Twitter).
        </p>
      </div>
    </Section>
  )
}

/* ---------- sección: Pasarelas ---------- */

function SecPagos({
  get, set, showSecrets, toggleSecret, onTestPaypal, onTestMp, paypalLoading, mpLoading,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
  showSecrets: Record<string, boolean>
  toggleSecret: (k: string) => void
  onTestPaypal: () => void
  onTestMp: () => void
  paypalLoading: boolean
  mpLoading: boolean
}) {
  const paypalActivo = isTrue(get('Fdp_Paypal_Activo'))
  const mpActivo = isTrue(get('Fdp_MP_Activo'))
  const transferActivo = isTrue(get('Fdp_Transfer_Activo'))
  const ppEnv = (get('Fdp_Paypal_Entorno') || 'sandbox').toLowerCase()
  const mpEnv = (get('Fdp_MP_Entorno') || 'sandbox').toLowerCase()

  return (
    <Section title="Pasarelas de pago" desc="Switch v1/v2, entornos sandbox/live y credenciales">
      {/* PayPal */}
      <div className="rounded-xl border border-ink-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid size-10 place-items-center rounded-lg bg-blue-50 font-bold text-blue-700">P</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-ink-900">PayPal</h3>
                <Badge variant={paypalActivo ? (ppEnv === 'live' ? 'success' : 'warning') : 'default'}>
                  {(get('Fdp_Paypal_Api_Version') || 'v2')} · {ppEnv}
                </Badge>
                {!paypalActivo && <Badge variant="default">inactivo</Badge>}
              </div>
              <p className="text-xs text-ink-500">Orders API + Webhook</p>
            </div>
          </div>
          <Switch checked={paypalActivo} onChange={(v) => set('Fdp_Paypal_Activo', v ? '1' : '0')} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Versión API"
            value={get('Fdp_Paypal_Api_Version') || 'v2'}
            onChange={(v) => set('Fdp_Paypal_Api_Version', v)}
            options={[
              { value: 'v2', label: 'v2 — Orders API (recomendada)' },
              { value: 'v1', label: 'v1 — Standard (legacy)' },
            ]}
          />
          <Select
            label="Entorno"
            value={ppEnv}
            onChange={(v) => set('Fdp_Paypal_Entorno', v)}
            options={[
              { value: 'sandbox', label: 'Sandbox' },
              { value: 'live', label: 'Live (producción)' },
            ]}
          />
          <Field label="Client ID (Live)" value={get('Fdp_Paypal_ClientID_Live')} onChange={(v) => set('Fdp_Paypal_ClientID_Live', v)} mono />
          <SecretField label="Secret (Live)" value={get('Fdp_Paypal_Secret_Live')} onChange={(v) => set('Fdp_Paypal_Secret_Live', v)} visible={!!showSecrets['Fdp_Paypal_Secret_Live']} onToggle={() => toggleSecret('Fdp_Paypal_Secret_Live')} />
          <Field label="Client ID (Sandbox)" value={get('Fdp_Paypal_ClientID_Sandbox')} onChange={(v) => set('Fdp_Paypal_ClientID_Sandbox', v)} mono />
          <SecretField label="Secret (Sandbox)" value={get('Fdp_Paypal_Secret_Sandbox')} onChange={(v) => set('Fdp_Paypal_Secret_Sandbox', v)} visible={!!showSecrets['Fdp_Paypal_Secret_Sandbox']} onToggle={() => toggleSecret('Fdp_Paypal_Secret_Sandbox')} />
          <div className="sm:col-span-2">
            <Field label="Webhook ID" value={get('Fdp_Paypal_Webhook_ID')} onChange={(v) => set('Fdp_Paypal_Webhook_ID', v)} mono />
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onTestPaypal}
            disabled={paypalLoading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
          >
            {paypalLoading && <Loader2 className="size-3 animate-spin" />}
            Probar credenciales PayPal
          </button>
        </div>
      </div>

      {/* Mercado Pago */}
      <div className="mt-4 rounded-xl border border-ink-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid size-10 place-items-center rounded-lg bg-cyan-50 font-bold text-cyan-700">M</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-ink-900">Mercado Pago</h3>
                <Badge variant={mpActivo ? (mpEnv === 'live' ? 'success' : 'warning') : 'default'}>
                  {(get('Fdp_MP_Api_Version') || 'brick')} · {mpEnv}
                </Badge>
                {!mpActivo && <Badge variant="default">inactivo</Badge>}
              </div>
              <p className="text-xs text-ink-500">Card Payment Brick + Webhook</p>
            </div>
          </div>
          <Switch checked={mpActivo} onChange={(v) => set('Fdp_MP_Activo', v ? '1' : '0')} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Versión API"
            value={get('Fdp_MP_Api_Version') || 'brick'}
            onChange={(v) => set('Fdp_MP_Api_Version', v)}
            options={[
              { value: 'brick', label: 'Brick (recomendada)' },
              { value: 'legacy', label: 'Checkout Pro (legacy)' },
            ]}
          />
          <Select
            label="Entorno"
            value={mpEnv}
            onChange={(v) => set('Fdp_MP_Entorno', v)}
            options={[
              { value: 'sandbox', label: 'Sandbox' },
              { value: 'live', label: 'Live (producción)' },
            ]}
          />
          <Field label="Public Key (Live)" value={get('Fdp_MP_Brick_PublicKey_Live')} onChange={(v) => set('Fdp_MP_Brick_PublicKey_Live', v)} mono />
          <SecretField label="Access Token (Live)" value={get('Fdp_MP_Brick_AccessToken_Live')} onChange={(v) => set('Fdp_MP_Brick_AccessToken_Live', v)} visible={!!showSecrets['Fdp_MP_Brick_AccessToken_Live']} onToggle={() => toggleSecret('Fdp_MP_Brick_AccessToken_Live')} />
          <Field label="Public Key (Sandbox)" value={get('Fdp_MP_Brick_PublicKey_Sandbox')} onChange={(v) => set('Fdp_MP_Brick_PublicKey_Sandbox', v)} mono />
          <SecretField label="Access Token (Sandbox)" value={get('Fdp_MP_Brick_AccessToken_Sandbox')} onChange={(v) => set('Fdp_MP_Brick_AccessToken_Sandbox', v)} visible={!!showSecrets['Fdp_MP_Brick_AccessToken_Sandbox']} onToggle={() => toggleSecret('Fdp_MP_Brick_AccessToken_Sandbox')} />
          <div className="sm:col-span-2">
            <SecretField label="Webhook Secret" value={get('Fdp_MP_Webhook_Secret')} onChange={(v) => set('Fdp_MP_Webhook_Secret', v)} visible={!!showSecrets['Fdp_MP_Webhook_Secret']} onToggle={() => toggleSecret('Fdp_MP_Webhook_Secret')} />
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onTestMp}
            disabled={mpLoading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
          >
            {mpLoading && <Loader2 className="size-3 animate-spin" />}
            Probar credenciales MP
          </button>
        </div>
      </div>

      {/* Transferencia */}
      <div className="mt-4 rounded-xl border border-ink-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid size-10 place-items-center rounded-lg bg-emerald-50 font-bold text-emerald-700">T</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-ink-900">Transferencia bancaria</h3>
                <Badge variant={transferActivo ? 'success' : 'default'}>
                  {transferActivo ? 'activa' : 'inactiva'}
                </Badge>
              </div>
              <p className="text-xs text-ink-500">Pago manual con confirmación de comprobante</p>
            </div>
          </div>
          <Switch checked={transferActivo} onChange={(v) => set('Fdp_Transfer_Activo', v ? '1' : '0')} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Banco" value={get('Fdp_Transfer_Banco')} onChange={(v) => set('Fdp_Transfer_Banco', v)} />
          <Field label="Beneficiario" value={get('Fdp_Transfer_Beneficiario')} onChange={(v) => set('Fdp_Transfer_Beneficiario', v)} />
          <Field label="Cuenta" value={get('Fdp_Transfer_Cuenta')} onChange={(v) => set('Fdp_Transfer_Cuenta', v)} mono />
          <Field label="CLABE" value={get('Fdp_Transfer_Clabe')} onChange={(v) => set('Fdp_Transfer_Clabe', v)} mono />
          <Field label="RFC" value={get('Fdp_Transfer_RFC')} onChange={(v) => set('Fdp_Transfer_RFC', v)} mono />
        </div>
      </div>
    </Section>
  )
}

/* ---------- sección: Aplazo (SFTP settlement + timeout) ---------- */

function SecAplazo({
  get, set, showSecrets, toggleSecret,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
  showSecrets: Record<string, boolean>
  toggleSecret: (k: string) => void
}) {
  return (
    <Section
      title="Aplazo"
      desc="Conciliación de liquidaciones vía SFTP semanal + timeout de loans pendientes (UC-07 + SCEN-04)"
    >
      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
        <strong>SFTP de liquidaciones:</strong> Aplazo entrega semanalmente un archivo
        CSV con los pagos transferidos. Estas credenciales las da Aplazo en el onboarding
        de producción. El cron se ejecuta los lunes a las 10:00 o manualmente desde la
        página <strong>Liquidaciones Aplazo</strong>.
      </div>

      <h3 className="mb-3 text-sm font-semibold text-ink-900">Credenciales SFTP</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Host SFTP"
          value={get('Aplazo_SFTP_Host')}
          onChange={(v) => set('Aplazo_SFTP_Host', v)}
          placeholder="sftp.aplazo.mx"
          mono
        />
        <Field
          label="Puerto"
          value={get('Aplazo_SFTP_Port')}
          onChange={(v) => set('Aplazo_SFTP_Port', v)}
          type="number"
          placeholder="22"
          mono
        />
        <Field
          label="Usuario"
          value={get('Aplazo_SFTP_User')}
          onChange={(v) => set('Aplazo_SFTP_User', v)}
          placeholder="mazautopartes"
          mono
        />
        <SecretField
          label="Password"
          value={get('Aplazo_SFTP_Pass')}
          onChange={(v) => set('Aplazo_SFTP_Pass', v)}
          visible={!!showSecrets['Aplazo_SFTP_Pass']}
          onToggle={() => toggleSecret('Aplazo_SFTP_Pass')}
        />
        <div className="sm:col-span-2">
          <Field
            label="Path remoto"
            value={get('Aplazo_SFTP_Path')}
            onChange={(v) => set('Aplazo_SFTP_Path', v)}
            placeholder="/settlements"
            mono
          />
          <p className="mt-1 text-[11px] text-ink-500">
            Carpeta del SFTP donde Aplazo deja los CSV (default: <span className="font-mono">/</span>).
          </p>
        </div>
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Timeout de loans pendientes</h3>
      <p className="mb-3 text-xs text-ink-500">
        SCEN-04: cuántas horas esperar antes de cancelar automáticamente un loan que
        siguió en estado PENDING sin que el cliente complete el pago. El cron{' '}
        <span className="font-mono">/api/cron/aplazo-cleanup-pending</span> corre cada 6h y
        usa este valor.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Horas para auto-cancelar (default 24)"
          value={get('Aplazo_Timeout_Hours')}
          onChange={(v) => set('Aplazo_Timeout_Hours', v)}
          type="number"
          placeholder="24"
        />
      </div>
    </Section>
  )
}

/* ---------- sección: Envíos ---------- */

function SecEnvios({
  get, set, showSecrets, toggleSecret,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
  showSecrets: Record<string, boolean>
  toggleSecret: (k: string) => void
}) {
  return (
    <Section title="Envíos" desc="Política, costos default y guías automáticas">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Costo de envío default (MXN)" value={get('Envio_CostoDefault')} onChange={(v) => set('Envio_CostoDefault', v)} type="number" />
        <Field label="Envío gratis a partir de (MXN)" value={get('Envio_GratisDesde')} onChange={(v) => set('Envio_GratisDesde', v)} type="number" />
        <Field label="Tiempo de entrega mínimo (días)" value={get('Envio_DiasMin')} onChange={(v) => set('Envio_DiasMin', v)} type="number" />
        <Field label="Tiempo de entrega máximo (días)" value={get('Envio_DiasMax')} onChange={(v) => set('Envio_DiasMax', v)} type="number" />
      </div>

      <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Tarifas fijas por transportista
      </h4>
      <p className="mb-3 text-[11px] text-ink-500">
        Estas tarifas son las que se guardan como costo de la guía (no el precio real del
        agregador). El admin no elige precio al generar la guía.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Tarifa fija Estafeta (MXN)" value={get('Shipping_Carrier_Estafeta_Rate')} onChange={(v) => set('Shipping_Carrier_Estafeta_Rate', v)} type="number" placeholder="160" />
        <Field label="Tarifa fija FedEx (MXN)" value={get('Shipping_Carrier_Fedex_Rate')} onChange={(v) => set('Shipping_Carrier_Fedex_Rate', v)} type="number" placeholder="260" />
      </div>

      <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Credenciales de guías (solo servidor)
      </h4>
      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong>⚠️ Nunca se exponen al navegador.</strong> Estafeta se genera vía XFT Logistics;
        FedEx usa la cuenta propia (queda en espera hasta capturar credenciales).
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SecretField label="XFT API Key (Estafeta)" value={get('XFT_ApiKey')} onChange={(v) => set('XFT_ApiKey', v)} visible={!!showSecrets['XFT_ApiKey']} onToggle={() => toggleSecret('XFT_ApiKey')} />
        <div className="hidden sm:block" />
        <SecretField label="FedEx — Client Id" value={get('FedEx_ApiKey')} onChange={(v) => set('FedEx_ApiKey', v)} visible={!!showSecrets['FedEx_ApiKey']} onToggle={() => toggleSecret('FedEx_ApiKey')} />
        <SecretField label="FedEx — Client Secret" value={get('FedEx_SecretKey')} onChange={(v) => set('FedEx_SecretKey', v)} visible={!!showSecrets['FedEx_SecretKey']} onToggle={() => toggleSecret('FedEx_SecretKey')} />
        <Field label="FedEx — Número de cuenta" value={get('FedEx_AccountNumber')} onChange={(v) => set('FedEx_AccountNumber', v)} mono />
      </div>

      <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Remitente (origen de la guía)
      </h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Nombre / contacto" value={get('Shipping_Sender_Name')} onChange={(v) => set('Shipping_Sender_Name', v)} placeholder="Maz Autopartes" />
        <Field label="Razón social" value={get('Shipping_Sender_BusinessName')} onChange={(v) => set('Shipping_Sender_BusinessName', v)} placeholder="Maz Autopartes" />
        <Field label="Teléfono" value={get('Shipping_Sender_Phone')} onChange={(v) => set('Shipping_Sender_Phone', v)} placeholder="3315710108" />
        <Field label="Correo (opcional)" value={get('Shipping_Sender_Email')} onChange={(v) => set('Shipping_Sender_Email', v)} type="email" />
        <Field label="Calle" value={get('Shipping_Sender_Street')} onChange={(v) => set('Shipping_Sender_Street', v)} placeholder="Av. Mariano Otero" />
        <Field label="Número" value={get('Shipping_Sender_Number')} onChange={(v) => set('Shipping_Sender_Number', v)} placeholder="5730-4" />
        <Field label="Colonia" value={get('Shipping_Sender_Neighborhood')} onChange={(v) => set('Shipping_Sender_Neighborhood', v)} placeholder="Paseos del Sol" />
        <Field label="CP origen" value={get('Shipping_Sender_Zip')} onChange={(v) => set('Shipping_Sender_Zip', v)} placeholder="45079" />
        <Field label="Ciudad" value={get('Shipping_Sender_City')} onChange={(v) => set('Shipping_Sender_City', v)} placeholder="Zapopan" />
        <Field label="Estado" value={get('Shipping_Sender_State')} onChange={(v) => set('Shipping_Sender_State', v)} placeholder="Jalisco" />
        <Field label="País (ISO)" value={get('Shipping_Sender_Country')} onChange={(v) => set('Shipping_Sender_Country', v)} placeholder="MX" />
        <Field label="Referencias (opcional)" value={get('Shipping_Sender_References')} onChange={(v) => set('Shipping_Sender_References', v)} />
      </div>

      <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Paquete default (cuando el producto no lo define)
      </h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Clave SAT default" value={get('Shipping_Default_SatCategory')} onChange={(v) => set('Shipping_Default_SatCategory', v)} mono placeholder="25172000" />
        <Field label="Tipo de empaque" value={get('Shipping_Default_PackageType')} onChange={(v) => set('Shipping_Default_PackageType', v)} placeholder="caja" />
        <Field label="Peso default (kg)" value={get('Shipping_Default_Weight')} onChange={(v) => set('Shipping_Default_Weight', v)} type="number" placeholder="2" />
        <Field label="Valor declarado default (MXN)" value={get('Shipping_Default_PackageValue')} onChange={(v) => set('Shipping_Default_PackageValue', v)} type="number" placeholder="1000" />
        <Field label="Largo default (cm)" value={get('Shipping_Default_Length')} onChange={(v) => set('Shipping_Default_Length', v)} type="number" placeholder="30" />
        <Field label="Ancho default (cm)" value={get('Shipping_Default_Width')} onChange={(v) => set('Shipping_Default_Width', v)} type="number" placeholder="20" />
        <Field label="Alto default (cm)" value={get('Shipping_Default_Height')} onChange={(v) => set('Shipping_Default_Height', v)} type="number" placeholder="15" />
      </div>

      <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Medidas de paquete por paquetería
      </h4>
      <p className="mb-3 text-[11px] text-ink-500">
        Estas son las medidas globales de la caja que se envía por cada paquetería; se usan al
        generar la guía.
      </p>

      <div className="rounded-xl border border-ink-200 p-4">
        <h5 className="mb-3 text-sm font-semibold text-ink-900">Estafeta</h5>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Peso (kg)" value={get('Shipping_Box_Estafeta_Weight')} onChange={(v) => set('Shipping_Box_Estafeta_Weight', v)} type="number" placeholder="5" />
          <Field label="Largo (cm)" value={get('Shipping_Box_Estafeta_Length')} onChange={(v) => set('Shipping_Box_Estafeta_Length', v)} type="number" placeholder="30" />
          <Field label="Ancho (cm)" value={get('Shipping_Box_Estafeta_Width')} onChange={(v) => set('Shipping_Box_Estafeta_Width', v)} type="number" placeholder="25" />
          <Field label="Alto (cm)" value={get('Shipping_Box_Estafeta_Height')} onChange={(v) => set('Shipping_Box_Estafeta_Height', v)} type="number" placeholder="30" />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-ink-200 p-4">
        <h5 className="mb-3 text-sm font-semibold text-ink-900">FedEx</h5>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Peso (kg)" value={get('Shipping_Box_Fedex_Weight')} onChange={(v) => set('Shipping_Box_Fedex_Weight', v)} type="number" placeholder="29" />
          <Field label="Largo (cm)" value={get('Shipping_Box_Fedex_Length')} onChange={(v) => set('Shipping_Box_Fedex_Length', v)} type="number" placeholder="60" />
          <Field label="Ancho (cm)" value={get('Shipping_Box_Fedex_Width')} onChange={(v) => set('Shipping_Box_Fedex_Width', v)} type="number" placeholder="20" />
          <Field label="Alto (cm)" value={get('Shipping_Box_Fedex_Height')} onChange={(v) => set('Shipping_Box_Fedex_Height', v)} type="number" placeholder="120" />
        </div>
      </div>
    </Section>
  )
}

/* ---------- sección: Email ---------- */

function SecEmail({
  get, set, showSecrets, toggleSecret,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
  showSecrets: Record<string, boolean>
  toggleSecret: (k: string) => void
}) {
  return (
    <Section title="Email transaccional" desc="Configura quién manda y quién recibe los avisos de pedido">
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong>💡 Sin estos valores configurados, ningún correo se envía</strong> (la app loguea warning silencioso).
        Usa <strong>Resend</strong> (recomendado, simple) o <strong>SMTP</strong> tradicional.
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="From — remitente"
            value={get('Email_From')}
            onChange={(v) => set('Email_From', v)}
            placeholder="Maz Autopartes <no-reply@mazautopartes.net>"
          />
        </div>
        <div className="sm:col-span-2">
          <Field
            label="A quién avisar de nuevos pedidos (Admin To)"
            value={get('Email_AdminTo')}
            onChange={(v) => set('Email_AdminTo', v)}
            placeholder="jesus@mazautopartes.net, ventas@mazautopartes.net"
          />
          <p className="mt-1 text-[11px] text-ink-500">Lista separada por comas. Estos correos reciben "admin_new_order", stock bajo, pedidos pendientes, etc.</p>
        </div>
      </div>

      <h4 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-ink-500">Opción A · Resend (recomendado)</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <SecretField
            label="Resend API Key"
            value={get('Email_Resend_ApiKey')}
            onChange={(v) => set('Email_Resend_ApiKey', v)}
            visible={!!showSecrets['Email_Resend_ApiKey']}
            onToggle={() => toggleSecret('Email_Resend_ApiKey')}
          />
          <p className="mt-1 text-[11px] text-ink-500">Crea cuenta gratis en resend.com (100 mails/día). El API key empieza con <code>re_</code>.</p>
        </div>
      </div>

      <h4 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-ink-500">Opción B · SMTP tradicional</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="SMTP host" value={get('Email_SMTP_Host')} onChange={(v) => set('Email_SMTP_Host', v)} mono placeholder="smtp.gmail.com" />
        <Field label="SMTP port" value={get('Email_SMTP_Port')} onChange={(v) => set('Email_SMTP_Port', v)} type="number" placeholder="587" />
        <Field label="SMTP user" value={get('Email_SMTP_User')} onChange={(v) => set('Email_SMTP_User', v)} mono />
        <SecretField label="SMTP pass" value={get('Email_SMTP_Pass')} onChange={(v) => set('Email_SMTP_Pass', v)} visible={!!showSecrets['Email_SMTP_Pass']} onToggle={() => toggleSecret('Email_SMTP_Pass')} />
        <Field label="SMTP secure (TLS)" value={get('Email_SMTP_Secure')} onChange={(v) => set('Email_SMTP_Secure', v)} placeholder="true / false" />
      </div>

      <div className="mt-5 border-t border-ink-200 pt-4">
        <TestEmailButton />
      </div>
    </Section>
  )
}

function TestEmailButton() {
  const [to, setTo] = useState('')
  const testMut = useMutation<{ ok: true; provider?: string }, Error, { to: string }>({
    mutationFn: (v) => apiPost('/api/admin/config/test-email', v),
  })
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1">
        <label className="mb-1 block text-xs font-semibold text-ink-700">
          Enviar email de prueba a:
        </label>
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="tu@correo.com"
          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
        />
      </div>
      <button
        type="button"
        disabled={!to || testMut.isPending}
        onClick={() => testMut.mutate({ to })}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {testMut.isPending ? '⏳ Enviando…' : '📨 Enviar prueba'}
      </button>
      {testMut.isSuccess && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700">
          ✓ Enviado vía {testMut.data.provider}
        </div>
      )}
      {testMut.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {(testMut.error as Error).message}
        </div>
      )}
    </div>
  )
}

/* ---------- sección: WhatsApp Business ---------- */

function SecWhatsApp({
  get, set, showSecrets, toggleSecret,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
  showSecrets: Record<string, boolean>
  toggleSecret: (k: string) => void
}) {
  const [testTo, setTestTo] = useState('')
  const [testMsg, setTestMsg] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const onShipped = isTrue(get('WA_OnShipped'))

  async function onSendTest() {
    setResult(null)
    if (!testTo.trim()) {
      setResult({ ok: false, msg: 'Falta el teléfono destino (E.164 con +52)' })
      return
    }
    setSending(true)
    try {
      const res = await apiPost<{ ok: boolean; messageId?: string; error?: string; skipped?: boolean }>(
        '/api/admin/whatsapp/test',
        { to: testTo.trim(), text: testMsg.trim() || undefined },
      )
      if (res.ok) {
        setResult({ ok: true, msg: `Enviado (id ${res.messageId || 'sin-id'})` })
      } else if (res.skipped) {
        setResult({ ok: false, msg: `Omitido: ${res.error || 'sin credenciales'}` })
      } else {
        setResult({ ok: false, msg: `Falló: ${res.error || 'error desconocido'}` })
      }
    } catch (e: any) {
      setResult({ ok: false, msg: e?.message || 'Error de red' })
    } finally {
      setSending(false)
    }
  }

  return (
    <Section title="WhatsApp Business Cloud" desc="Credenciales API + envío de prueba">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="API URL"
            value={get('WA_API_URL')}
            onChange={(v) => set('WA_API_URL', v)}
            placeholder="https://graph.facebook.com/v18.0"
            mono
          />
        </div>
        <Field
          label="Phone Number ID"
          value={get('WA_PHONE_ID')}
          onChange={(v) => set('WA_PHONE_ID', v)}
          mono
        />
        <SecretField
          label="Access Token"
          value={get('WA_TOKEN')}
          onChange={(v) => set('WA_TOKEN', v)}
          visible={!!showSecrets['WA_TOKEN']}
          onToggle={() => toggleSecret('WA_TOKEN')}
        />
      </div>

      <div className="mt-5 flex items-center gap-3 rounded-xl border border-ink-200 p-4">
        <Switch
          checked={onShipped}
          onChange={(v) => set('WA_OnShipped', v ? '1' : '0')}
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-900">Notificar por WhatsApp al marcar pedido como enviado</div>
          <p className="text-xs text-ink-500">
            Cuando un pedido cambia a status <span className="font-mono">shipped</span> y tiene tracking + teléfono,
            además del email se envía un WhatsApp al cliente con la guía.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-ink-200 bg-ink-50/50 p-4">
        <h3 className="mb-3 text-sm font-semibold text-ink-900">Envío de prueba</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Teléfono destino (E.164)"
            value={testTo}
            onChange={setTestTo}
            placeholder="+5215555555555"
            mono
          />
          <Field
            label="Mensaje (opcional)"
            value={testMsg}
            onChange={setTestMsg}
            placeholder="Hola desde Maz Autopartes"
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onSendTest}
            disabled={sending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
            Enviar prueba
          </button>
          {result && (
            <span
              className={`text-xs ${
                result.ok ? 'text-emerald-700' : 'text-red-700'
              }`}
            >
              {result.msg}
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-ink-500">
          Sin template, el mensaje se envía como "free-form" — sólo funciona dentro de la ventana de
          24h o con el número en la lista de prueba de Meta.
        </p>
      </div>
    </Section>
  )
}

/* ---------- sección: Mantenimiento ---------- */

function SecMantenimiento({ get, set }: { get: (k: string) => string; set: (k: string, v: string) => void }) {
  const activo = isTrue(get('Mantenimiento_Activo'))
  return (
    <Section title="Mantenimiento" desc="Modo offline y banner global">
      <div className="flex items-center gap-3 rounded-xl border border-ink-200 p-4">
        <Switch checked={activo} onChange={(v) => set('Mantenimiento_Activo', v ? '1' : '0')} />
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-900">Modo mantenimiento</div>
          <p className="text-xs text-ink-500">Cuando esté activo, el sitio público mostrará el mensaje configurado.</p>
        </div>
        <Badge variant={activo ? 'danger' : 'success'}>{activo ? 'sitio offline' : 'sitio online'}</Badge>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3">
        <Field
          label="Título de la página"
          value={get('Mantenimiento_Titulo')}
          onChange={(v) => set('Mantenimiento_Titulo', v)}
          placeholder="Estamos en mantenimiento"
        />
        <Textarea
          label="Mensaje principal"
          value={get('Mantenimiento_Mensaje')}
          onChange={(v) => set('Mantenimiento_Mensaje', v)}
          rows={3}
        />
        <Field
          label="Texto botón WhatsApp"
          value={get('Mantenimiento_BotonWhatsApp')}
          onChange={(v) => set('Mantenimiento_BotonWhatsApp', v)}
          placeholder="Escríbenos por WhatsApp"
        />
        <Field
          label="Pie de página (footnote)"
          value={get('Mantenimiento_Footnote')}
          onChange={(v) => set('Mantenimiento_Footnote', v)}
          placeholder="Disculpa las molestias."
        />
        <Textarea
          label="Banner global (cuando NO está activo el mantenimiento)"
          value={get('Banner_Texto')}
          onChange={(v) => set('Banner_Texto', v)}
          rows={2}
        />
      </div>
    </Section>
  )
}

/* ---------- sección: Home ---------- */

function SecHome({ get, set }: { get: (k: string) => string; set: (k: string, v: string) => void }) {
  const coberturaActivo = isTrue(get('Home_Banner_Cobertura_Activo'))
  return (
    <Section
      title="Home"
      desc="Textos editables del Hero, banners y categorías destacadas"
    >
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Título del Hero"
          value={get('Home_Hero_Titulo')}
          onChange={(v) => set('Home_Hero_Titulo', v)}
          placeholder="Encuentra la refacción exacta para tu auto."
        />
        <Textarea
          label="Subtítulo del Hero"
          value={get('Home_Hero_Subtitulo')}
          onChange={(v) => set('Home_Hero_Subtitulo', v)}
          rows={2}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="CTA 1 (Texto)"
            value={get('Home_Hero_CTA1_Texto')}
            onChange={(v) => set('Home_Hero_CTA1_Texto', v)}
            placeholder="Buscar refacción"
          />
          <Field
            label="CTA 2 (Texto)"
            value={get('Home_Hero_CTA2_Texto')}
            onChange={(v) => set('Home_Hero_CTA2_Texto', v)}
            placeholder="Por mi vehículo"
          />
        </div>
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Banner de cobertura</h3>
      <div className="flex items-center gap-3 rounded-xl border border-ink-200 p-4">
        <Switch
          checked={coberturaActivo}
          onChange={(v) => set('Home_Banner_Cobertura_Activo', v ? '1' : '0')}
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-900">Mostrar banner "32 estados"</div>
          <p className="text-xs text-ink-500">
            Sección llamativa sobre cobertura nacional. Si está apagada, no se renderiza en la home.
          </p>
        </div>
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Categorías destacadas</h3>
      <Field
        label="Slugs separados por coma (vacío = todas)"
        value={get('Home_Categorias_Mostrar')}
        onChange={(v) => set('Home_Categorias_Mostrar', v)}
        placeholder="frenos,mecanica,electrico"
        mono
      />
      <p className="mt-2 text-xs text-ink-500">
        Ej: <span className="font-mono">frenos,mecanica,electrico</span>. Útil para destacar pocas
        categorías en la home sin tocar código.
      </p>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Iconos del strip (footer)</h3>
      <p className="mb-3 text-xs text-ink-500">
        Personaliza los 4 iconos del strip del footer (envío, garantía, asesoría, cupón).
        Cada uno admite un icono de Lucide, una imagen subida o una URL externa. Si lo
        dejas vacío, se usa el SVG por defecto.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-ink-700">Icono envío</label>
          <IconPicker
            value={get('Footer_Strip_Icon_Shipping')}
            onChange={(v) => set('Footer_Strip_Icon_Shipping', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-ink-700">Icono garantía</label>
          <IconPicker
            value={get('Footer_Strip_Icon_Warranty')}
            onChange={(v) => set('Footer_Strip_Icon_Warranty', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-ink-700">Icono asesoría</label>
          <IconPicker
            value={get('Footer_Strip_Icon_Support')}
            onChange={(v) => set('Footer_Strip_Icon_Support', v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-ink-700">Icono cupón</label>
          <IconPicker
            value={get('Footer_Strip_Icon_Coupon')}
            onChange={(v) => set('Footer_Strip_Icon_Coupon', v)}
          />
        </div>
      </div>
    </Section>
  )
}

/* ---------- sección: Trust y entrega ---------- */

function SecTrust({ get, set }: { get: (k: string) => string; set: (k: string, v: string) => void }) {
  return (
    <Section
      title="Trust y entrega"
      desc="Claims, horarios, garantías y cupones que se muestran en toda la web pública. Cambiar estos valores actualiza la home, fichas de producto, footer, checkout, etc."
    >
      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
        <strong>💡 Tip:</strong> los placeholders <span className="font-mono">{'{min}'}</span> y{' '}
        <span className="font-mono">{'{days}'}</span> en los textos de Trust se sustituyen
        automáticamente con el monto mínimo de envío gratis y los días de devolución.
      </div>

      {/* Cobertura y entrega */}
      <h3 className="mb-3 text-sm font-semibold text-ink-900">Cobertura y entrega</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="Texto de cobertura"
            value={get('Site_DeliveryCoverage')}
            onChange={(v) => set('Site_DeliveryCoverage', v)}
            placeholder="los 32 estados de México"
          />
        </div>
        <Field
          label="Tiempo de entrega nacional"
          value={get('Site_DeliveryTime')}
          onChange={(v) => set('Site_DeliveryTime', v)}
          placeholder="24-72h"
        />
        <Field
          label="Tiempo de entrega zona metropolitana"
          value={get('Site_DeliveryTimeMetro')}
          onChange={(v) => set('Site_DeliveryTimeMetro', v)}
          placeholder="24-48h"
        />
        <Field
          label="Mínimo para envío gratis (MXN)"
          value={get('Site_FreeShippingMin')}
          onChange={(v) => set('Site_FreeShippingMin', v)}
          type="number"
          placeholder="1500"
        />
      </div>

      {/* Horarios */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Horarios de atención</h3>
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Horario corto (footer / topbar)"
          value={get('Site_BusinessHoursShort')}
          onChange={(v) => set('Site_BusinessHoursShort', v)}
          placeholder="9 a 19 h"
        />
        <Field
          label="Horario completo (contacto / about)"
          value={get('Site_BusinessHoursLong')}
          onChange={(v) => set('Site_BusinessHoursLong', v)}
          placeholder="Lunes a viernes 9 a 19 h · Sábados 9 a 14 h"
        />
        <Field
          label="Horario para schema.org (openingHours)"
          value={get('Site_BusinessHoursSchema')}
          onChange={(v) => set('Site_BusinessHoursSchema', v)}
          placeholder="Mo-Fr 09:00-19:00, Sa 09:00-14:00"
          mono
        />
        <p className="text-xs text-ink-500">
          El formato schema.org es estándar de microdata
          (<span className="font-mono">Mo,Tu,We,Th,Fr,Sa,Su</span> + rangos HH:MM).
        </p>
      </div>

      {/* Garantías y políticas */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Garantías y políticas</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Días de garantía"
          value={get('Site_WarrantyDays')}
          onChange={(v) => set('Site_WarrantyDays', v)}
          type="number"
          placeholder="30"
        />
        <Field
          label="Días para devolución"
          value={get('Site_ReturnDays')}
          onChange={(v) => set('Site_ReturnDays', v)}
          type="number"
          placeholder="30"
        />
        <Field
          label="Tasa de impuesto (decimal)"
          value={get('Site_TaxRate')}
          onChange={(v) => set('Site_TaxRate', v)}
          placeholder="0.16"
          mono
        />
        <Field
          label="Etiqueta del impuesto"
          value={get('Site_TaxLabel')}
          onChange={(v) => set('Site_TaxLabel', v)}
          placeholder="IVA 16%"
        />
      </div>

      {/* Cupón de bienvenida */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Cupón de bienvenida</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field
          label="Código"
          value={get('Coupon_Welcome_Code')}
          onChange={(v) => set('Coupon_Welcome_Code', v)}
          placeholder="BIENVENIDO"
          mono
        />
        <Field
          label="Descuento (%)"
          value={get('Coupon_Welcome_Discount')}
          onChange={(v) => set('Coupon_Welcome_Discount', v)}
          type="number"
          placeholder="10"
        />
        <Field
          label="Etiqueta visible"
          value={get('Coupon_Welcome_Label')}
          onChange={(v) => set('Coupon_Welcome_Label', v)}
          placeholder="-10% en tu primera compra"
        />
      </div>

      {/* Métricas de catálogo */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Métricas de catálogo (marketing)</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Total de productos (claim)"
          value={get('Catalog_TotalProductsClaim')}
          onChange={(v) => set('Catalog_TotalProductsClaim', v)}
          placeholder="300 mil productos"
        />
        <Field
          label="Total de estados (claim)"
          value={get('Catalog_TotalStatesClaim')}
          onChange={(v) => set('Catalog_TotalStatesClaim', v)}
          placeholder="32 estados"
        />
      </div>

      {/* Trust strip */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Trust strip y claims</h3>
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Envío gratis (acepta {min})"
          value={get('Trust_Free_Shipping')}
          onChange={(v) => set('Trust_Free_Shipping', v)}
          placeholder="Envío gratis desde {min}"
        />
        <Field
          label="Compra protegida"
          value={get('Trust_Protected_Purchase')}
          onChange={(v) => set('Trust_Protected_Purchase', v)}
          placeholder="Compra protegida"
        />
        <Field
          label="Devolución (acepta {days})"
          value={get('Trust_Return_Days')}
          onChange={(v) => set('Trust_Return_Days', v)}
          placeholder="Devolución en {days} días"
        />
        <Field
          label="Pago seguro"
          value={get('Trust_Secure_Payment')}
          onChange={(v) => set('Trust_Secure_Payment', v)}
          placeholder="Pago seguro SSL"
        />
        <Field
          label="Asesoría real"
          value={get('Trust_Real_Support')}
          onChange={(v) => set('Trust_Real_Support', v)}
          placeholder="Asesoría real por WhatsApp"
        />
      </div>

      {/* Strip del footer (4 columnas) */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Strip del footer (4 columnas)</h3>
      <p className="mb-3 text-xs text-ink-500">
        Textos de la tira que aparece en el footer. La columna de envío gratis usa{' '}
        <span className="font-mono">Envío gratis (acepta {'{min}'})</span> de arriba y el monto real
        de <span className="font-mono">/envios</span>; aquí editas su subtítulo y la columna de garantía.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Envío gratis — subtítulo"
          value={get('Footer_FreeShippingSub')}
          onChange={(v) => set('Footer_FreeShippingSub', v)}
          placeholder="A los 32 estados de México"
        />
        <div className="hidden sm:block" />
        <Field
          label="Garantía — título"
          value={get('Footer_Claim_Warranty')}
          onChange={(v) => set('Footer_Claim_Warranty', v)}
          placeholder="Garantía total"
        />
        <Field
          label="Garantía — subtítulo"
          value={get('Footer_Claim_Warranty_Sub')}
          onChange={(v) => set('Footer_Claim_Warranty_Sub', v)}
          placeholder="Refacciones nuevas, 100% originales"
        />
      </div>

      {/* Defaults legacy */}
      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink-900">Defaults legacy (fallback)</h3>
      <p className="mb-3 text-xs text-ink-500">
        Valores que el código sigue usando como respaldo si los principales (sección "Sitio") no están configurados.
        Idealmente coinciden con <span className="font-mono">Sitio_Telefono</span>,{' '}
        <span className="font-mono">Sitio_Whatsapp</span> y <span className="font-mono">Sitio_Email</span>.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Teléfono fallback"
          value={get('Site_PhoneFallback')}
          onChange={(v) => set('Site_PhoneFallback', v)}
          placeholder="+52 33 1234 5678"
          mono
        />
        <Field
          label="WhatsApp fallback"
          value={get('Site_WhatsAppFallback')}
          onChange={(v) => set('Site_WhatsAppFallback', v)}
          placeholder="+52 33 1234 5678"
          mono
        />
        <div className="sm:col-span-2">
          <Field
            label="Email fallback"
            value={get('Site_EmailFallback')}
            onChange={(v) => set('Site_EmailFallback', v)}
            placeholder="info@mazautopartes.net"
            mono
          />
        </div>
      </div>
    </Section>
  )
}

/* ---------- helper: imagen con preview + upload + banco ---------- */

/**
 * Campo para una imagen administrable: muestra preview, deja subir un
 * archivo nuevo (POST /api/admin/image-library), abrir el banco modal
 * o pegar una URL externa. El valor que persiste en `config` es siempre
 * una string con la URL final.
 */
function BannerImageField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadError(null)
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/image-library', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = (await r.json().catch(() => ({}))) as {
        ok?: boolean
        data?: { url?: string }
        error?: string
      }
      if (!r.ok || !data.ok || !data.data?.url) {
        throw new Error(data.error || `HTTP ${r.status}`)
      }
      onChange(data.data.url)
    } catch (err) {
      setUploadError((err as Error).message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="rounded-xl border border-ink-200 bg-ink-50/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-semibold text-ink-700">{label}</label>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-500 hover:text-brand-600"
          >
            <RotateCcw className="size-3" /> Quitar
          </button>
        )}
      </div>

      <div className="mb-3 flex items-center gap-3">
        <div className="grid h-20 w-32 shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-200 bg-white">
          {value ? (
            <img
              src={value}
              alt={label}
              className="h-full w-full object-cover"
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).style.opacity = '0.3'
              }}
            />
          ) : (
            <ImageIcon className="size-7 text-ink-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/uploads/library/… o https://…"
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
          />
          {hint && <p className="mt-1 text-[11px] text-ink-500">{hint}</p>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          onChange={onFileChange}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
          {uploading ? 'Subiendo…' : 'Subir imagen'}
        </button>
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-100"
        >
          <FolderOpen className="size-3.5" />
          Elegir del banco
        </button>
      </div>

      {uploadError && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
          {uploadError}
        </div>
      )}

      <ImageLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => {
          onChange(url)
          setLibraryOpen(false)
        }}
      />
    </div>
  )
}

/* ---------- sección: Banner principal (TimeTravel) ---------- */

function SecBannerPrincipal({
  get,
  set,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
}) {
  return (
    <Section
      title="Banner principal (TimeTravel)"
      desc="Banner ancho con CTA que aparece bajo el carousel principal de la home. Si dejas vacíos eyebrow + título + CTA, el banner no se renderiza."
    >
      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
        <strong>Tip:</strong> los colores de fondo y del botón usan las
        variables de marca configuradas en <span className="font-mono">/admin/branding</span>{' '}
        (no hay hex hardcoded). La imagen se monta como background con un
        overlay del color base para garantizar legibilidad del texto blanco.
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Eyebrow (texto pequeño rojo)"
          value={get('TimeTravel_Eyebrow')}
          onChange={(v) => set('TimeTravel_Eyebrow', v)}
          placeholder="Promoción de la semana"
        />
        <Textarea
          label="Título principal"
          value={get('TimeTravel_Title')}
          onChange={(v) => set('TimeTravel_Title', v)}
          rows={2}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Texto del botón (CTA)"
            value={get('TimeTravel_CtaLabel')}
            onChange={(v) => set('TimeTravel_CtaLabel', v)}
            placeholder="Ver más"
          />
          <Field
            label="URL del botón (CTA)"
            value={get('TimeTravel_CtaUrl')}
            onChange={(v) => set('TimeTravel_CtaUrl', v)}
            placeholder="/catalogo"
            mono
          />
        </div>
        <BannerImageField
          label="Imagen de fondo"
          value={get('TimeTravel_Image')}
          onChange={(v) => set('TimeTravel_Image', v)}
          hint="Recomendado 1600×400 (JPG/WebP). Se monta como background-image cubriendo todo el ancho."
        />
      </div>
    </Section>
  )
}

/* ---------- sección: Banners secundarios (AdBanner) ---------- */

function SecBannerAd({
  get,
  set,
}: {
  get: (k: string) => string
  set: (k: string, v: string) => void
}) {
  return (
    <Section
      title="Banners secundarios (AdBanner)"
      desc="2 cards con foto al fondo y CTA azul que aparecen bajo el carousel de ofertas. Si dejas vacía toda una card, no se renderiza."
    >
      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
        <strong>Tip:</strong> los colores del fondo oscuro y del botón
        respetan las variables de marca configuradas en{' '}
        <span className="font-mono">/admin/branding</span>. La imagen se
        muestra al fondo con opacidad y un overlay para que el texto blanco
        siempre sea legible.
      </div>

      {/* Card 1 */}
      <div className="rounded-xl border border-ink-200 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Badge variant="default">Card 1</Badge>
          <h3 className="text-sm font-semibold text-ink-900">Lado izquierdo</h3>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field
            label="Título"
            value={get('AdBanner1_Title')}
            onChange={(v) => set('AdBanner1_Title', v)}
            placeholder="Aceites premium"
          />
          <Field
            label="Subtítulo"
            value={get('AdBanner1_Sub')}
            onChange={(v) => set('AdBanner1_Sub', v)}
            placeholder="Hasta 20% off en lubricantes de motor"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Texto del botón"
              value={get('AdBanner1_CtaLabel')}
              onChange={(v) => set('AdBanner1_CtaLabel', v)}
              placeholder="Ver más"
            />
            <Field
              label="URL del botón"
              value={get('AdBanner1_CtaUrl')}
              onChange={(v) => set('AdBanner1_CtaUrl', v)}
              placeholder="/categoria/aceites"
              mono
            />
          </div>
          <BannerImageField
            label="Imagen de fondo"
            value={get('AdBanner1_Image')}
            onChange={(v) => set('AdBanner1_Image', v)}
            hint="Recomendado 800×400 (JPG/WebP)."
          />
        </div>
      </div>

      {/* Card 2 */}
      <div className="mt-4 rounded-xl border border-ink-200 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Badge variant="default">Card 2</Badge>
          <h3 className="text-sm font-semibold text-ink-900">Lado derecho</h3>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field
            label="Título"
            value={get('AdBanner2_Title')}
            onChange={(v) => set('AdBanner2_Title', v)}
            placeholder="Frenos premium -20%"
          />
          <Field
            label="Subtítulo"
            value={get('AdBanner2_Sub')}
            onChange={(v) => set('AdBanner2_Sub', v)}
            placeholder="Bosch, Akebono, NTK"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Texto del botón"
              value={get('AdBanner2_CtaLabel')}
              onChange={(v) => set('AdBanner2_CtaLabel', v)}
              placeholder="Ver más"
            />
            <Field
              label="URL del botón"
              value={get('AdBanner2_CtaUrl')}
              onChange={(v) => set('AdBanner2_CtaUrl', v)}
              placeholder="/ofertas"
              mono
            />
          </div>
          <BannerImageField
            label="Imagen de fondo"
            value={get('AdBanner2_Image')}
            onChange={(v) => set('AdBanner2_Image', v)}
            hint="Recomendado 800×400 (JPG/WebP)."
          />
        </div>
      </div>
    </Section>
  )
}
